import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  ArrowLeft, Shield, AlertTriangle, CheckCircle, Clock, XCircle,
  Loader2, RefreshCw, Building2, Users, AlertCircle
} from "lucide-react";
import { API } from "@/App";

export const AdminReportsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [activeTab, setActiveTab] = useState("insurance");
  const [insuranceReport, setInsuranceReport] = useState(null);
  const [parityReport, setParityReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (activeTab === "insurance") {
      fetchInsuranceReport();
    } else if (activeTab === "landlord-parity") {
      fetchParityReport();
    }
  }, [activeTab]);

  const fetchInsuranceReport = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/contractors/insurance-report`, { headers });
      setInsuranceReport(response.data);
    } catch (error) {
      toast.error("Failed to load insurance report");
    } finally {
      setLoading(false);
    }
  };

  const fetchParityReport = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/landlord-parity-report`, { headers });
      setParityReport(response.data);
    } catch (error) {
      toast.error("Failed to load parity report");
    } finally {
      setLoading(false);
    }
  };

  const runInsuranceCheck = async () => {
    setChecking(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/contractors/check-insurance`, {}, { headers });
      toast.success("Insurance check completed");
      fetchInsuranceReport();
    } catch (error) {
      toast.error("Failed to run insurance check");
    } finally {
      setChecking(false);
    }
  };

  const getStatusBadge = (insuranceStatus, daysUntil) => {
    if (insuranceStatus === "no_insurance") {
      return <Badge className="bg-slate-100 text-slate-700">No Insurance</Badge>;
    } else if (insuranceStatus === "expired") {
      return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    } else if (insuranceStatus === "expiring_soon") {
      return <Badge className="bg-amber-100 text-amber-700">{daysUntil} days left</Badge>;
    } else if (insuranceStatus === "valid") {
      return <Badge className="bg-green-100 text-green-700">Valid</Badge>;
    }
    return <Badge variant="outline">Unknown</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/lettings")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Admin Reports</h1>
            <p className="text-sm text-muted-foreground">Insurance and data integrity reports</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="insurance" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Contractor Insurance
            </TabsTrigger>
            <TabsTrigger value="landlord-parity" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Landlord Property Parity
            </TabsTrigger>
          </TabsList>

          {/* Insurance Report Tab */}
          <TabsContent value="insurance" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : insuranceReport ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-slate-700">{insuranceReport.summary.total}</div>
                      <div className="text-sm text-muted-foreground">Total</div>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{insuranceReport.summary.active}</div>
                      <div className="text-sm text-muted-foreground">Active</div>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200">
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-amber-600">{insuranceReport.summary.expiring_soon}</div>
                      <div className="text-sm text-muted-foreground">Expiring Soon</div>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200">
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-red-600">{insuranceReport.summary.expired}</div>
                      <div className="text-sm text-muted-foreground">Expired</div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-slate-600">{insuranceReport.summary.suspended}</div>
                      <div className="text-sm text-muted-foreground">Suspended</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Action Button */}
                <div className="flex justify-end">
                  <Button onClick={runInsuranceCheck} disabled={checking}>
                    {checking ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Run Insurance Check Now
                  </Button>
                </div>

                {/* Expiring Soon */}
                {insuranceReport.expiring_soon.length > 0 && (
                  <Card className="border-amber-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-5 h-5" />
                        Expiring Soon ({insuranceReport.expiring_soon.length})
                      </CardTitle>
                      <CardDescription>Insurance expiring within 30 days</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contractor</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Expiry Date</TableHead>
                            <TableHead>Days Left</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {insuranceReport.expiring_soon.map(c => (
                            <TableRow key={c.contractor_id}>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell>{c.email}</TableCell>
                              <TableCell>{c.expiry_date}</TableCell>
                              <TableCell>
                                <Badge className="bg-amber-100 text-amber-700">{c.days_until_expiry} days</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Expired */}
                {insuranceReport.expired.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <XCircle className="w-5 h-5" />
                        Expired Insurance ({insuranceReport.expired.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contractor</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Expiry Date</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {insuranceReport.expired.map(c => (
                            <TableRow key={c.contractor_id}>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell>{c.email}</TableCell>
                              <TableCell>{c.expiry_date}</TableCell>
                              <TableCell>
                                <Badge className={c.status === "suspended" ? "bg-red-100 text-red-700" : "bg-slate-100"}>
                                  {c.status || "active"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* No Insurance */}
                {insuranceReport.no_insurance.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-slate-500" />
                        No Insurance Uploaded ({insuranceReport.no_insurance.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contractor</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {insuranceReport.no_insurance.map(c => (
                            <TableRow key={c.contractor_id}>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell>{c.email}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{c.status || "N/A"}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </TabsContent>

          {/* Landlord Parity Tab */}
          <TabsContent value="landlord-parity" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : parityReport ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-slate-700">{parityReport.summary.total_landlords}</div>
                      <div className="text-sm text-muted-foreground">Total Landlords</div>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200">
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-red-600">{parityReport.summary.with_mismatches}</div>
                      <div className="text-sm text-muted-foreground">With Mismatches</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-blue-600">{parityReport.summary.total_admin_properties}</div>
                      <div className="text-sm text-muted-foreground">Admin Properties</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{parityReport.summary.total_portal_properties}</div>
                      <div className="text-sm text-muted-foreground">Portal Properties</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Landlord Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Landlord Property Comparison</CardTitle>
                    <CardDescription>
                      Comparing properties linked in admin vs. visible in landlord portal
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Landlord</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="text-center">Admin Count</TableHead>
                          <TableHead className="text-center">Portal Count</TableHead>
                          <TableHead>Portal Access</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parityReport.landlords.slice(0, 50).map(ll => (
                          <TableRow key={ll.landlord_id} className={ll.has_mismatch ? "bg-red-50" : ""}>
                            <TableCell>
                              {ll.has_mismatch ? (
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{ll.name}</TableCell>
                            <TableCell className="text-sm text-slate-500">{ll.email || "-"}</TableCell>
                            <TableCell className="text-center">{ll.admin_property_count}</TableCell>
                            <TableCell className="text-center">{ll.portal_property_count}</TableCell>
                            <TableCell>
                              {ll.has_portal_access ? (
                                <Badge className="bg-green-100 text-green-700">Yes</Badge>
                              ) : (
                                <Badge variant="outline">No</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {parityReport.landlords.length > 50 && (
                      <p className="text-sm text-muted-foreground text-center mt-4">
                        Showing first 50 of {parityReport.landlords.length} landlords
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : null}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminReportsPage;
